// Enhanced CrewMember.js model with availability support
// Update to src/models/CrewMember.js

export class CrewMember {
  constructor(data = {}) {
    this.id = data.id || this.generateId();
    this.name = data.name || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.assigned_services = data.assigned_services || [];
    this.is_active = data.is_active !== undefined ? data.is_active : true;
    this.color = data.color;
    this.is_visible_to_customers = data.is_visible_to_customers;
    this.availability = data.availability || this.getDefaultAvailability();
    // NEW: Work frequency fields
    this.work_frequency_weeks = data.work_frequency_weeks || 1;
    this.work_reference_date = data.work_reference_date || null;
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at || new Date().toISOString();
  }

  generateId() {
    return 'crew_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // NEW: Default availability structure (all days off by default)
  getDefaultAvailability() {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const availability = {};

    days.forEach(day => {
      availability[day] = {
        isWorking: false,
        timeRanges: []
      };
    });

    return availability;
  }

  // NEW: Set availability for a specific day
  setDayAvailability(dayName, isWorking, timeRanges = []) {
    if (!this.availability) {
      this.availability = this.getDefaultAvailability();
    }

    this.availability[dayName] = {
      isWorking,
      timeRanges: timeRanges || []
    };
    this.updated_at = new Date().toISOString();
  }

  // NEW: Check if crew member is working on a specific week
  isWorkingOnWeek(date) {
    // If working every week (default behavior)
    if (this.work_frequency_weeks === 1) {
      return true;
    }

    // If no reference date set, cannot determine working weeks
    if (!this.work_reference_date) {
      return false;
    }

    // Calculate week difference from reference date
    const targetDate = new Date(date);
    const refDate = new Date(this.work_reference_date);

    // Get the start of the week (Sunday) for both dates
    const targetWeekStart = new Date(targetDate);
    targetWeekStart.setDate(targetDate.getDate() - targetDate.getDay());

    const refWeekStart = new Date(refDate);
    refWeekStart.setDate(refDate.getDate() - refDate.getDay());

    // Calculate week difference
    const weekDiff = Math.floor((targetWeekStart - refWeekStart) / (7 * 24 * 60 * 60 * 1000));

    // Check if crew member works on this week
    return weekDiff % this.work_frequency_weeks === 0;
  }

  // NEW: Get formatted work frequency description for display
  getWorkFrequencyDescription() {
    if (this.work_frequency_weeks === 1) {
      return 'Every week';
    }

    if (!this.work_reference_date) {
      return `Every ${this.work_frequency_weeks} weeks (no reference date set)`;
    }

    const refDate = new Date(this.work_reference_date);
    const formattedDate = refDate.toLocaleDateString();

    return `Every ${this.work_frequency_weeks} weeks (from ${formattedDate})`;
  }

  // NEW: Set work frequency
  setWorkFrequency(weeks, referenceDate = null) {
    this.work_frequency_weeks = weeks;
    this.work_reference_date = weeks > 1 ? referenceDate : null;
    this.updated_at = new Date().toISOString();
  }

  // NEW: Add time range to a specific day
  addTimeRange(dayName, startTime, endTime) {
    if (!this.availability || !this.availability[dayName]) {
      this.setDayAvailability(dayName, true, []);
    }

    const timeRange = { start: startTime, end: endTime };
    this.availability[dayName].timeRanges.push(timeRange);
    this.availability[dayName].isWorking = true;
    this.updated_at = new Date().toISOString();
  }

  // NEW: Remove time range from a specific day
  removeTimeRange(dayName, rangeIndex) {
    if (!this.availability || !this.availability[dayName] || !this.availability[dayName].timeRanges) {
      return;
    }

    this.availability[dayName].timeRanges.splice(rangeIndex, 1);

    // If no time ranges left, set day as not working
    if (this.availability[dayName].timeRanges.length === 0) {
      this.availability[dayName].isWorking = false;
    }

    this.updated_at = new Date().toISOString();
  }

  // NEW: Check if crew member is available at specific day and time
  isAvailableAt(dayName, time) {
    if (!this.availability || !this.availability[dayName] || !this.availability[dayName].isWorking) {
      return false;
    }

    const timeRanges = this.availability[dayName].timeRanges || [];
    return timeRanges.some(range => {
      return time >= range.start && time <= range.end;
    });
  }

  // NEW: Get availability conflict info for appointment
  getAvailabilityConflict(appointmentDate, appointmentTime) {
    // Convert date to day name
    const date = new Date(appointmentDate);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];

    const isAvailable = this.isAvailableAt(dayName, appointmentTime);

    if (isAvailable) {
      return { hasConflict: false };
    }

    // Get available time ranges for the conflict message
    const dayAvailability = this.availability?.[dayName];
    const availableRanges = dayAvailability?.isWorking ? dayAvailability.timeRanges : [];

    return {
      hasConflict: true,
      dayName,
      isWorkingDay: dayAvailability?.isWorking || false,
      availableRanges
    };
  }

  // NEW: Get formatted availability summary for display
  getAvailabilitySummary() {
    if (!this.availability) return 'No availability set';

    const workingDays = Object.entries(this.availability)
      .filter(([day, data]) => data.isWorking)
      .map(([day, data]) => {
        const ranges = data.timeRanges.map(range => `${range.start}-${range.end}`).join(', ');
        return `${this.capitalizeDay(day)}: ${ranges}`;
      });

    return workingDays.length > 0 ? workingDays.join(' | ') : 'No working days set';
  }

  // Helper method to capitalize day names
  capitalizeDay(dayName) {
    const dayMap = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };
    return dayMap[dayName] || dayName;
  }

  // Existing validation methods with availability validation added
  static validate(data) {
    const errors = [];

    if (!data.name || data.name.trim().length === 0) {
      errors.push('Name is required');
    }

    if (data.name && data.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }

    if (!data.color || data.color.trim().length === 0) {
      errors.push('Color is required');
    }

    if (data.email && !this.isValidEmail(data.email)) {
      errors.push('Invalid email format');
    }

    if (data.phone && !this.isValidPhone(data.phone)) {
      errors.push('Invalid phone format');
    }

    if (data.assigned_services && !Array.isArray(data.assigned_services)) {
      errors.push('Assigned services must be an array');
    }

    if (data.assigned_services && data.assigned_services.length === 0) {
      errors.push('At least one service must be assigned');
    }

    if (data.work_frequency_weeks && data.work_frequency_weeks < 1) {
      errors.push('Work frequency must be at least 1 week');
    }

    if (data.work_frequency_weeks && data.work_frequency_weeks > 8) {
      errors.push('Work frequency cannot exceed 8 weeks');
    }

    if (data.work_frequency_weeks > 1 && !data.work_reference_date) {
      errors.push('Reference date is required when work frequency is more than 1 week');
    }

    if (data.assigned_services && Array.isArray(data.assigned_services)) {
      const invalidServices = data.assigned_services.filter(serviceId =>
        !serviceId || typeof serviceId !== 'string' || serviceId.trim().length === 0
      );
      if (invalidServices.length > 0) {
        errors.push('All assigned services must be valid service IDs');
      }
    }

    // NEW: Validate availability structure
    if (data.availability && !this.isValidAvailability(data.availability)) {
      errors.push('Invalid availability structure');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // NEW: Validate availability data structure
  static isValidAvailability(availability) {
    if (!availability || typeof availability !== 'object') {
      return false;
    }

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    for (const day of days) {
      if (availability[day]) {
        const dayData = availability[day];

        // Check if isWorking is boolean
        if (typeof dayData.isWorking !== 'boolean') {
          return false;
        }

        // Check if timeRanges is array
        if (!Array.isArray(dayData.timeRanges)) {
          return false;
        }

        // Validate each time range
        for (const range of dayData.timeRanges) {
          if (!range.start || !range.end || !this.isValidTime(range.start) || !this.isValidTime(range.end)) {
            return false;
          }

          // Check if start time is before end time
          if (range.start >= range.end) {
            return false;
          }
        }
      }
    }

    return true;
  }

  static isValidTime(time) {
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  static isValidColor(color) {
    const hexColorRegex = /^#[0-9A-F]{6}$/i;
    return hexColorRegex.test(color);
  }

  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static isValidPhone(phone) {
    // Allow various phone formats - basic validation
    return true;
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  // Existing methods...
  update(data) {
    Object.keys(data).forEach(key => {
      if (key !== 'id' && key !== 'created_at') {
        this[key] = data[key];
      }
    });
    this.updated_at = new Date().toISOString();
  }

  addService(serviceId) {
    if (!this.assigned_services.includes(serviceId)) {
      this.assigned_services.push(serviceId);
      this.updated_at = new Date().toISOString();
    }
  }

  removeService(serviceId) {
    this.assigned_services = this.assigned_services.filter(id => id !== serviceId);
    this.updated_at = new Date().toISOString();
  }

  hasService(serviceId) {
    return this.assigned_services.includes(serviceId);
  }

  clearAllServices() {
    this.assigned_services = [];
    this.updated_at = new Date().toISOString();
  }

  setServices(serviceIds) {
    this.assigned_services = Array.isArray(serviceIds) ? [...serviceIds] : [];
    this.updated_at = new Date().toISOString();
  }

  activate() {
    this.is_active = true;
    this.updated_at = new Date().toISOString();
  }

  deactivate() {
    this.is_active = false;
    this.updated_at = new Date().toISOString();
  }

  getInitials() {
    return this.name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getFormattedPhone() {
    if (!this.phone) return '';

    const cleaned = this.phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return this.phone;
  }

  // Updated toJSON to include availability
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      phone: this.phone,
      assigned_services: this.assigned_services,
      is_active: this.is_active,
      color: this.color,
      is_visible_to_customers: this.is_visible_to_customers,
      availability: this.availability,
      work_frequency_weeks: this.work_frequency_weeks,
      work_reference_date: this.work_reference_date,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  static fromJSON(json) {
    return new CrewMember(json);
  }

  // Existing conflict checking methods...
  static hasConflict(crewMemberId, date, time, duration, existingAppointments, excludeAppointmentId = null) {
    const appointmentStart = new Date(`${date}T${time}`);
    const appointmentEnd = new Date(appointmentStart.getTime() + duration * 60000);

    return existingAppointments.some(apt => {
      if (excludeAppointmentId && apt.id === excludeAppointmentId) return false;
      if (apt.crew_member_id !== crewMemberId) return false;
      if (apt.status === 'cancelled') return false;

      const existingStart = new Date(`${apt.date}T${apt.time}`);
      const existingEnd = new Date(existingStart.getTime() + apt.duration * 60000);

      return (appointmentStart < existingEnd && appointmentEnd > existingStart);
    });
  }

  static canPerformService(crewMember, serviceId) {
    return crewMember.assigned_services.includes(serviceId);
  }

  static getCrewMembersForService(crewMembers, serviceId) {
    return crewMembers.filter(member =>
      member.is_active && member.assigned_services.includes(serviceId)
    );
  }

  static getServicesForCrewMember(crewMember) {
    return crewMember.assigned_services || [];
  }
}